import React, { useState, useCallback, useEffect, Fragment } from "react";
import { Link } from "react-router";
import PropTypes from "prop-types";
import MaskedInput from "react-text-mask";
import { SubNavBar, RKPayRibbon } from "@components/common";
import { JobService, OfficerService } from "@services";
import { OFFICER_VETTED_STATUS } from "@constants";
import utils from "@utils";
import { ScrollDetect, Image, Loader } from "../../../components";

import "./OfficerDetails.scss";

const dateMask = [/[0-1]/, /[0-9]/, "/", /[0-3]/, /[0-9]/, "/", /2/, /0/, /[2-9]/, /[0-9]/];
const OfficerDetails = ({ router, user }) => {
	const { lang } = global;

	const [jobHistory, setJobHistory] = useState([]);
	const [callOffHistory, setCallOffHistory] = useState([]);
	const [filteredJobHistory, setFilteredJobHistory] = useState([]);
	const [filteredCallOffHistory, setFilteredCallOffHistory] = useState([]);
	const [jobCount, setJobCount] = useState(undefined);
	const [historyPageNumber, setHistoryPageNumber] = useState(1);
	const [visible, setVisible] = useState(false);
	const [listHasMore, setListHasMore] = useState(true);

	const [loadingDetails, setLoadingDetails] = useState(true);
	const [officer, setOfficer] = useState({});
	const [officerStatus, setOfficerStatus] = useState(false);

	const [jobStartDate, setJobStartDate] = useState("");
	const [jobEndDate, setJobEndDate] = useState("");
	const [hasMultiplier, setHasMultiplier] = useState(false);
	const [hoursMultiplier, setHoursMultiplier] = useState(1);
	const [hoursWorked, setHoursWorked] = useState(0);
	const [superAdminHistory, setSuperAdminHistory] = useState([]);
	const [currentTab, setCurrentTab] = useState(1);

	/** Retrieves officer details based on the officers unique id */
	const getOfficerById = () => {
		setLoadingDetails(true);
		// Set the following each time so that if navigating from notifications while already looking at another officer's details
		// none of the previous officer information is leftover and mixed with the new officer's info.
		setJobHistory([]);
		setFilteredJobHistory([]);
		setJobCount();
		setListHasMore(true);
		OfficerService.getById(router.params.officerId)
			.then(({ data }) => {
				setOfficer(data);
				setOfficerStatus(
					OFFICER_VETTED_STATUS.find(item => item.value === data.VettedStatus),
				);
			})
			.catch(({ response: { data } }) => {
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error loading officer"),
					title: "Error",
				});
			})
			.finally(() => setLoadingDetails(false));
	};

	const getCallOffs = () => {
		setCallOffHistory([]);
		OfficerService.getCallOffs(router.params.officerId)
			.then(({ data }) => {
				setCallOffHistory([...data]);
				setFilteredCallOffHistory([...data]);
			})
			.catch(({ response: { data } }) => {
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error loading call offs"),
					title: "Error",
				});
			});
	};

	/**
	 * A callback method which will retrieve a list of jobs the officer is assigned to
	 * @param {Number} displayCount The anount of jobs that will bbe displayed
	 */
	const getOfficerJobs = useCallback((displayCount = 25) => {
		const queryParams = {
			displayCount: user.isSuperAdmin() ? 250 : displayCount,
			pageNumber: historyPageNumber,
		};
		JobService.getAllByOfficerId(router.params.officerId, queryParams)
			.then(({ data }) => {
				const newJobs = data.ItemList ?? [];
				setListHasMore(newJobs.length >= queryParams.displayCount);
				setJobCount(data.Count);
				setJobHistory([...jobHistory, ...newJobs]);
				setFilteredJobHistory([...jobHistory, ...newJobs]);
				setHistoryPageNumber(historyPageNumber + 1);
				window.scrollTo(0, 0);
			})
			.catch(({ response: { data } }) => {
				setListHasMore(false);
				setJobCount(0);
				setJobHistory([]);
				setFilteredJobHistory([]);
				setHistoryPageNumber(1);
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error loading job history"),
					title: "Error",
				});
			});
	});

	const onClickBack = event => {
		event.preventDefault();
		router.push("/officers");
	};

	const handleChangeTab = value => {
		setCurrentTab(value);
	};

	/**
	 * Updates the filters which will filter the list of jobs assigned to an the officer
	 * @param {String} property The filter which will be updated
	 * @param {String} value The value captured from the filter
	 */
	const updateSuperAdminFilters = (property, value) => {
		switch (property) {
			case "jobStartDate":
				setJobStartDate(value);
				break;
			case "jobEndDate":
				setJobEndDate(value);
				break;
			case "hasMultiplier":
				setHasMultiplier(!hasMultiplier);
				break;
			case "hoursMultiplier":
				setHoursMultiplier(value.match(/^\d*\.?\d{0,2}$/g) ? value : hoursMultiplier);
				break;
			default:
				break;
		}
	};

	/**
	 * Validates dates strings based on the browser
	 * @param {String} date the start or end date of the job
	 * @returns {Boolean} the validated date string after compared to a regular expression
	 */
	const isDateValid = date => {
		if (date === "") return false;
		const dateRegex =
			utils.isDesktopSafari || utils.isIE
				? /(\d{2})\/(\d{2})\/(\d{4})/
				: /(\d{4})-(\d{2})-(\d{2})/;
		return dateRegex.test(date);
	};

	// Call when officerId parameter in the url is changed, including when component is mounted for the first time
	// Not using a traditional mounting effect hook since notifications can route to this page
	useEffect(() => {
		getOfficerById();
		getCallOffs();
	}, [router.params.officerId]);

	// Reset visibility of the scrolldetect element after it has become visible once
	useEffect(() => {
		if (visible) {
			getOfficerJobs();
			setVisible(false);
		}
	}, [visible]);

	useEffect(() => {
		if (filteredJobHistory.length < 10 && listHasMore) {
			getOfficerJobs();
		}
	}, [filteredJobHistory]);

	useEffect(() => {
		let newHistory = [...jobHistory];
		let newCallOffs = [...callOffHistory];
		if (isDateValid(jobStartDate)) {
			// Only include jobs that start on or after the given date
			newHistory = newHistory.filter(({ StartDateTime }) => {
				const startDate = new Date(
					`${jobStartDate}${utils.isDesktopSafari || utils.isIE ? " 00:00" : "T00:00:00"}`,
				);
				const jobStart = new Date(`${StartDateTime}Z`);
				return jobStart.getTime() >= startDate.getTime();
			});
			newCallOffs = newCallOffs.filter(({ CallOffDate }) => {
				const startDate = new Date(
					`${jobStartDate}${utils.isDesktopSafari || utils.isIE ? " 00:00" : "T00:00:00"}`,
				);
				const jobStart = new Date(`${CallOffDate}Z`);
				return jobStart.getTime() >= startDate.getTime();
			});
		}

		if (isDateValid(jobEndDate)) {
			// Only include jobs that end on or before the given date
			newHistory = newHistory.filter(({ EndDateTime }) => {
				const endDate = new Date(
					`${jobEndDate}${utils.isDesktopSafari || utils.isIE ? " 23:59" : "T23:59:59"}`,
				);
				const jobEnd = new Date(`${EndDateTime}Z`);
				return jobEnd.getTime() <= endDate.getTime();
			});
			newCallOffs = newCallOffs.filter(({ CallOffDate }) => {
				const endDate = new Date(
					`${jobEndDate}${utils.isDesktopSafari || utils.isIE ? " 23:59" : "T23:59:59"}`,
				);
				const jobEnd = new Date(`${CallOffDate}Z`);
				return jobEnd.getTime() <= endDate.getTime();
			});
		}

		setFilteredJobHistory(newHistory);
		setFilteredCallOffHistory(newCallOffs);
		// Calculate total number of hours based on a weekend time multiplier and the scheduled hours for each remaining job
		newHistory = newHistory.map(job => {
			const jobStart = new Date(`${job.StartDateTime}Z`);
			const startDOW = jobStart.getDay();
			if (hasMultiplier) {
				const scheduled =
					job.ScheduledHours *
					(startDOW === 0 || startDOW === 6 ? parseFloat(hoursMultiplier) || 1 : 1);
				return {
					...job,
					MultipliedHours: Math.round((scheduled + Number.EPSILON) * 100) / 100,
				};
			}

			return {
				...job,
				MultipliedHours: Math.round((job.ScheduledHours + Number.EPSILON) * 100) / 100,
			};
		});

		setSuperAdminHistory(newHistory);
		const jobHours = newHistory.map(({ MultipliedHours }) => MultipliedHours);
		// Round to two decimal places after summing all hours available
		if (jobHours.length > 0)
			setHoursWorked(
				Math.round(
					(jobHours.reduce((total, current) => total + current) + Number.EPSILON) * 100,
				) / 100,
			);
	}, [jobHistory.length, jobStartDate, jobEndDate, hasMultiplier, hoursMultiplier]);

	const voidRecord = item => {
		const auxList = [...callOffHistory];
		const newItem = auxList.find(val => val.Id === item.Id);
		newItem.Deleted = true;
		OfficerService.voidCallOff(newItem)
			.then(() => {
				setCallOffHistory(auxList);
				setFilteredCallOffHistory(auxList);
			})
			.catch(({ response: { data } }) => {
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error voiding call off"),
					title: "Error",
				});
			});
	};

	const restoreRecord = item => {
		const auxList = [...callOffHistory];
		const newItem = auxList.find(val => val.Id === item.Id);
		newItem.Deleted = false;
		OfficerService.restoreCallOff(newItem)
			.then(() => {
				setCallOffHistory(auxList);
				setFilteredCallOffHistory(auxList);
			})
			.catch(({ response: { data } }) => {
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error restoring call off"),
					title: "Error",
				});
			});
	};
	return (
		<Fragment>
			<SubNavBar
				backText={lang.t("Back to list")}
				backButton
				onClickBack={onClickBack}
			/>
			<div
				id="officer-details"
				className="container"
			>
				<div
					className="row"
					id="page-title"
				>
					<h1>{lang.t("Officer Details")}</h1>
				</div>
				<div className="row">
					<div
						id="officer-info"
						className="col-sm-5 col-md-4"
					>
						<div className="pane container-fluid">
							<Choose>
								<When condition={loadingDetails}>
									<Loader active />
								</When>
								<Otherwise>
									<div className="text-center">
										<Image
											src={officer.ContactViewModel.ContactPhotoUrl || ""}
											fallbackSrc="avatar"
										/>
										<h3>{`${officer.ContactViewModel.FirstName} ${officer.ContactViewModel.LastName}`}</h3>
										<div>{officer.OfficerRank}</div>
										<If
											condition={
												(user.isLEA() || user.isSuperAdmin()) &&
												officerStatus
											}
										>
											<div
												id="officer-vetted-status"
												className={`text-center label label-${officerStatus.color}`}
											>
												{officerStatus.label}
											</div>
										</If>
										<br />
										<Choose>
											<When
												condition={
													officer.VettedStatus === 2 ||
													officer.VettedStatus === 8
												}
											>
												{lang.t("Verified")}{" "}
												{utils.formatDate(
													`${officer.LastVettedDate}Z`,
													"en-US",
													user.TimeZoneId,
													"numeric",
												)}
											</When>
											<When condition={officer.VettedStatus === 0}>
												{lang.t("Created")}{" "}
												{utils.formatDate(
													`${officer.LastVettedDate}Z`,
													"en-US",
													user.TimeZoneId,
													"numeric",
												)}
											</When>
										</Choose>
									</div>
									<div className="separator separator-solid" />
									<div className="row">
										<div className="col-xs-5">
											<a
												className={`btn btn-link contact-button ${officer.Email ? "" : "hidden"}`}
												href={`mailto:${officer.Email}`}
											>
												<i className="rkf rkf-email" />
												{lang.t("EMail")}
											</a>
										</div>
										<div className="col-xs-7">
											<a
												className={`btn btn-link contact-button ${
													officer.ContactViewModel.MobilePhone
														? ""
														: "hidden"
												}`}
												href={`tel:${officer.ContactViewModel.MobilePhone}`}
											>
												<i className="rkf rkf-phone" />
												{utils.formatPhoneNumber(
													officer.ContactViewModel.MobilePhone,
												)}
											</a>
										</div>
									</div>
									<If condition={!Number.isNaN(jobCount)}>
										<div className="separator separator-solid" />
										<div className="text-center">
											<div>{lang.t("Total Jobs")}</div>
											<div>{jobCount}</div>
										</div>
									</If>
									<If condition={officer.ContactViewModel.IsRKPay}>
										<RKPayRibbon />
									</If>
								</Otherwise>
							</Choose>
						</div>
					</div>
					<div
						id="officer-job-history"
						className="col-sm-7 col-md-8"
					>
						<If condition={user.isSuperAdmin() || user.isLEA()}>
							<div className="row">
								<div className="col-xs-6 col-md-3 form-group">
									<label htmlFor="jobStartDate">{lang.t("Start Date")}</label>
									<Choose>
										<When condition={utils.isDesktopSafari || utils.isIE}>
											<MaskedInput
												name="jobStartDate"
												id="jobStartDate"
												type="text"
												mask={dateMask}
												max={jobEndDate}
												className="form-control"
												placeholder="mm/dd/yyyy"
												value={jobStartDate}
												onChange={({ target: { value } }) =>
													updateSuperAdminFilters("jobStartDate", value)
												}
											/>
										</When>
										<Otherwise>
											<input
												type="date"
												className="form-control"
												id="jobStartDate"
												max={jobEndDate}
												name="jobStartDate"
												value={jobStartDate}
												onChange={({ target: { value } }) =>
													updateSuperAdminFilters("jobStartDate", value)
												}
											/>
										</Otherwise>
									</Choose>
								</div>
								<div className="col-xs-6 col-md-3 form-group">
									<label htmlFor="jobEndDate">{lang.t("End Date")}</label>
									<Choose>
										<When condition={utils.isDesktopSafari || utils.isIE}>
											<MaskedInput
												name="jobEndDate"
												id="jobEndDate"
												type="text"
												mask={dateMask}
												className="form-control"
												min={jobStartDate}
												disabled={!jobStartDate}
												placeholder="mm/dd/yyyy"
												value={jobEndDate}
												onChange={({ target: { value } }) =>
													updateSuperAdminFilters("jobEndDate", value)
												}
											/>
										</When>
										<Otherwise>
											<input
												type="date"
												className="form-control"
												id="jobEndDate"
												name="jobEndDate"
												min={jobStartDate}
												disabled={!jobStartDate}
												value={jobEndDate}
												onChange={({ target: { value } }) =>
													updateSuperAdminFilters("jobEndDate", value)
												}
											/>
										</Otherwise>
									</Choose>
								</div>
								<If condition={user.isSuperAdmin()}>
									<div className="col-xs-6 col-md-4 form-group">
										{lang.t("Total Hours")}: <strong>{hoursWorked}</strong>
										<div className="checkbox">
											<label htmlFor="hasMultiplier">
												<input
													type="checkbox"
													id="hasMultiplier"
													name="hasMultiplier"
													checked={hasMultiplier}
													onChange={() =>
														updateSuperAdminFilters("hasMultiplier")
													}
												/>
												{lang.t("Weekend Multiplier")}
											</label>
										</div>
									</div>
									<div
										className={`col-xs-6 col-md-2 form-group ${hasMultiplier ? "" : "hidden"}`}
									>
										<label htmlFor="hoursMultiplier">
											{lang.t("Multiplier")}
										</label>
										<input
											type="tel"
											className="form-control"
											id="hoursMultiplier"
											name="hoursMultiplier"
											value={hoursMultiplier}
											onChange={({ target: { value } }) =>
												updateSuperAdminFilters("hoursMultiplier", value)
											}
										/>
									</div>
								</If>
							</div>
						</If>
						<div className="pane">
							<ul
								role="tablist"
								className="nav nav-tabs"
							>
								<li
									role="presentation"
									className={currentTab === 1 ? "active" : ""}
								>
									<span
										className="tab"
										onClick={() => handleChangeTab(1)}
										onKeyDown={() => handleChangeTab(1)}
										tabIndex={0}
										role="tab"
									>
										{lang.t("Job History")}
									</span>
								</li>
								<li
									role="presentation"
									className={currentTab === 2 ? "active" : ""}
								>
									<span
										className="tab"
										onClick={() => handleChangeTab(2)}
										onKeyDown={() => handleChangeTab(2)}
										tabIndex={0}
										role="tab"
									>
										{lang.t("Call Offs")}
									</span>
								</li>
							</ul>
							<div className="tab-pane active">
								<If condition={currentTab === 1}>
									<div
										id="table-body"
										className="container-fluid"
									>
										<If
											condition={
												!listHasMore && filteredJobHistory.length === 0
											}
										>
											<div className="row">
												<div className="col-xs-12 text-center">
													<span>{lang.t("No jobs worked")}</span>
												</div>
											</div>
										</If>
										<For
											each="item"
											of={
												user.isSuperAdmin()
													? superAdminHistory
													: filteredJobHistory
											}
										>
											<div
												className="row"
												key={`jobHistory-${item.Id}-${item.PositionId}`}
											>
												<div className="col-xs-6 col-xs-push-6 col-md-push-0 col-md-3 text-right">
													{utils.formatDate(
														`${item.StartDateTime}Z`,
														"en-US",
														user.TimeZoneId,
														"short",
													)}
												</div>
												<div className="col-xs-6 col-xs-pull-6 col-md-pull-0 col-md-3">
													<strong className="visible-xs-inline visible-sm-inline">
														{item.ClientName}
													</strong>
													<span className="hidden-xs hidden-sm">
														{item.ClientName}
													</span>
												</div>
												<div className="col-md-3">
													{item.LocationAddress}
													<br
														className={
															item.LocationAddress ? "" : "hidden"
														}
													/>
													{item.LocationCityStateZip}
												</div>
												<div className="col-md-3">
													<If condition={user.isSuperAdmin()}>
														<small>
															Scheduled Hours: {item.MultipliedHours}
														</small>
														<br />
													</If>
													<Link
														to={`/jobs/${item.Id}`}
														id={`go-to-job-${item.Id}`}
													>
														{lang.t("View Details")}
													</Link>
												</div>
											</div>
										</For>
										<If
											condition={
												(listHasMore && !user.isSuperAdmin()) ||
												(user.isSuperAdmin() && historyPageNumber === 1)
											}
										>
											<ScrollDetect
												className="text-center"
												onVisible={() => setVisible(true)}
											>
												<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
											</ScrollDetect>
										</If>
									</div>
								</If>
								<If condition={currentTab === 2}>
									<div
										id="table-body"
										className="container-fluid"
									>
										<If condition={filteredCallOffHistory.length === 0}>
											<div className="row">
												<div className="col-xs-12 text-center">
													<span>{lang.t("No call offs registered")}</span>
												</div>
											</div>
										</If>
										<For
											each="item"
											of={filteredCallOffHistory}
										>
											<div
												className="row"
												key={`jobHistory-${item.Id}`}
											>
												<div className="col-xs-6 col-xs-push-6 col-md-push-0 col-md-3 text-right">
													<div style={{ display: "grid" }}>
														{utils.formatDate(
															`${item.CallOffDate}Z`,
															"en-US",
															user.TimeZoneId,
															"short",
														)}
														<strong>{item.ClientName}</strong>
													</div>
												</div>
												<div className="col-xs-6 col-xs-pull-6 col-md-pull-0 col-md-3">
													<Link
														to={`/jobs/${item.JobEventId}`}
														id={`go-to-job-${item.JobEventId}`}
													>
														{lang.t("View Details")}
													</Link>
												</div>
												<div className="col-md-3">
													Reason: {item.Reason}
												</div>
												<div className="col-md-3">
													<If condition={item.Deleted === false}>
														<button
															type="button"
															className="btn btn-primary"
															onClick={() => voidRecord(item)}
														>
															{lang.t("Void")}
														</button>
													</If>
													<If condition={item.Deleted === true}>
														<button
															type="button"
															className="btn btn-danger"
															onClick={() => restoreRecord(item)}
														>
															{lang.t("Restore")}
														</button>
													</If>
												</div>
											</div>
										</For>
										<If
											condition={
												currentTab === 1 &&
												((listHasMore && !user.isSuperAdmin()) ||
													(user.isSuperAdmin() &&
														historyPageNumber === 1))
											}
										>
											<ScrollDetect
												className="text-center"
												onVisible={() => setVisible(true)}
											>
												<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
											</ScrollDetect>
										</If>
									</div>
								</If>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

OfficerDetails.propTypes = {
	router: PropTypes.object.isRequired,
	user: PropTypes.object,
	officerId: PropTypes.object,
};

OfficerDetails.defaultProps = {
	user: {},
	officerId: {},
};

export default OfficerDetails;
